import s from './MineSquad.module.sass';

import React, { FC, useEffect, useState } from 'react';
import {
  Block,
  Button,
  Header,
  InfoBlock,
  InlineHeader,
  List,
  ListItem,
  Placeholder,
  ScrollView,
} from '$uikit';
import { LoadingStatus } from '$store/reels/interface';
import { Api } from '$api';
import { useDispatch, useSelector } from 'react-redux';
import { toastActions } from '$store/toast';
import { CoinSquad } from '$store/models';
import { batchActions } from '$store';
import { mainActions } from '$store/main';
import { Loader, MineOnboarding, UnitLink, UnitName, UserCell } from '$shared/components';
import { useCurrentUserInfo, useTranslator, useUserInfo } from '$hooks';
import { Link, useRoute, useRouter } from 'react-router5';
import { IcChevron16, IcCopy28, IcLogo40 } from '$assets';
import copyToClipboard from 'copy-text-to-clipboard';
import { INVITE, MINE, MINE_SQUAD_BOOST } from '$shared/constants/pages';
import { prettifyNumber } from '$utils/number';
import { _confirm, getStorageItem } from '$utils';
import { mineActions, selectMine } from '$store/mine';
import { BottomSheet } from '$uikit/BottomSheet/BottomSheet';
import { TELEGRAM_BOT_USERNAME } from '$shared/constants';

export const MineSquad: FC = () => {
  const dispatch = useDispatch();
  const t = useTranslator();
  const router = useRouter();
  const [loadingStatus, setLoadingStatus] = useState<LoadingStatus>('loading');
  const [info, setInfo] = useState<{
    squad: CoinSquad;
    topItems: { userId: number; earnAmount: number }[];
    joined: boolean;
    inSquad: boolean;
    changeSquadCost: number;
  } | null>(null);
  const owner = useUserInfo(info?.squad.userId ?? 0);
  const { route } = useRoute();
  const squadId = route.params.id;
  const { onboardingPassed } = useSelector(selectMine);
  const curUser = useCurrentUserInfo();
  const isOwner = info?.squad.userId === curUser?.id;

  const loadData = () => {
    setLoadingStatus('loading');
    Api.get<any, any>(`/mine/squad/${squadId}`, {
      params: {
        onboarded: getStorageItem('mine_onboarding_passed') === 'yes' ? 1 : 0,
      },
    })
      .then((data) => {
        dispatch(
          batchActions(
            mainActions.setUser(data.squadOwner),
            mainActions.setUsers(data.users),
            mineActions.setOnboardingPassed(data.onboardingPassed),
          ),
        );
        setInfo({
          squad: data.squad,
          topItems: data.topItems,
          joined: data.joined,
          inSquad: data.inSquad,
          changeSquadCost: data.changeSquadCost,
        });
        setLoadingStatus('');
      })
      .catch((e) => {
        setLoadingStatus('failed');
        dispatch(toastActions.setToastFail(e.message));
      });
  };

  useEffect(() => {
    loadData();
  }, []);

  const handleJoin = () => {
    if (info && info.inSquad) {
      _confirm(
        t('mine_squad_change_confirm', {
          cost: info.changeSquadCost,
        }),
        () => {
          doJoin();
        },
      );
    } else {
      doJoin();
    }
  };

  const doJoin = () => {
    dispatch(toastActions.setToastLoading());
    Api.post(`/mine/squad/${squadId}/join`)
      .then(() => {
        dispatch(toastActions.setToastSuccess());
        setInfo((prev) => (prev ? { ...prev, joined: true } : null));
        router.navigate(MINE, {}, { replace: true });
      })
      .catch((e) => {
        dispatch(toastActions.setToastFail(e.message));
      });
  };

  const doCopyLink = (link: string) => {
    copyToClipboard(link);
    dispatch(toastActions.setToastSuccess(t('copied')));
  };

  const handleCopy = () => {
    const hexUserId = new Number(curUser.id).toString(16);
    const link = `ton.place/mine/squad/${squadId}?ref=${hexUserId}`;
    const telegramLink = `t.me/${TELEGRAM_BOT_USERNAME}/app?startapp=squad${squadId}_${curUser.id}`;

    dispatch(
      mainActions.setPopup(
        <BottomSheet title={t('mine_squad_copy_link_title')}>
          <div style={{ padding: `0 16px 16px` }}>
            <Link routeName={INVITE} onClick={() => dispatch(mainActions.setPopup(null))}>
              <InfoBlock type="info" title={t('mine_squad_copy_link_ref_title')} arrow>
                {t('mine_squad_copy_link_ref_text')}
              </InfoBlock>
            </Link>
            <List>
              <ListItem
                description={link}
                icon={<IcCopy28 />}
                onClick={() => doCopyLink('https://' + link)}
              >
                {t('mine_squad_copy_link_site_label')}
              </ListItem>
            </List>
            <List>
              <ListItem
                description={telegramLink}
                icon={<IcCopy28 />}
                onClick={() => doCopyLink('https://' + telegramLink)}
              >
                {t('mine_squad_copy_link_telegram_label')}
              </ListItem>
            </List>
          </div>
        </BottomSheet>,
      ),
    );
  };

  function renderContent() {
    if (loadingStatus === 'failed') {
      return (
        <Placeholder
          title={t('failed_title')}
          actions={<Button onClick={loadData}>{t('failed_button')}</Button>}
        >
          {t('failed_caption')}
        </Placeholder>
      );
    }

    if (loadingStatus === 'loading' || !info) {
      return <Loader isWrapped />;
    }

    const BoostElement = isOwner ? Link : 'div';
    return (
      <>
        <div className={s.revenueWrap}>
          <Block className={s.revenue}>
            <UnitLink unit={owner}>
              <div
                className={s.photo}
                style={{ backgroundImage: `url(${info.squad.url})` }}
              />
            </UnitLink>
            <UnitLink unit={owner} className={s.owner}>
              <UnitName unit={owner} />
              <IcChevron16 />
            </UnitLink>
            <div className={s.revenueLabel}>{t('mine_squad_earned')}:</div>
            <div className={s.revenueValueWrap}>
              <div className={s.revenueIcon}>
                <IcLogo40 />
              </div>
              <div className={s.revenueValue}>
                {prettifyNumber(info.squad.earnAmount)}
              </div>
            </div>
            <div className={s.revenueInfo}>{t('mine_squad_coins_give_info')}</div>
          </Block>
          <BoostElement
            // @ts-ignore
            routeName={MINE_SQUAD_BOOST}
            routeParams={{ id: squadId }}
            className={s.boostWrap}
          >
            <Block className={s.boost}>
              {t('mine_squad_boost')} +{info.squad.boost} {isOwner && <IcChevron16 />}
            </Block>
          </BoostElement>
          <div className={s.boostInfo}>{t('mine_squad_boost_info')}</div>
          <Button
            onClick={() => router.navigate(MINE)}
            mode="light"
            style={{ marginTop: 16 }}
          >
            {t('mine_go_to_game')}
          </Button>
        </div>
        <InlineHeader type="sub">
          {t('mine_squad_members')} {info.squad.membersCount}
        </InlineHeader>
        {info.topItems.map((item) => (
          <UserCell
            isFollowBtnShown={false}
            key={item.userId}
            objectId={item.userId}
            disabled
            after={
              <div className={s.memberEarned}>{prettifyNumber(item.earnAmount)}</div>
            }
          />
        ))}
      </>
    );
  }

  function renderFooterButton() {
    if (!info) {
      return null;
    }

    if (!info.joined) {
      return (
        <Button onClick={handleJoin} flare>
          {t('mine_squad_join')}
        </Button>
      );
    }

    return (
      <Button onClick={handleCopy} icon={<IcCopy28 />}>
        {t('mine_squad_copy_link')}
      </Button>
    );
  }

  // if (!onboardingPassed && loadingStatus === '') {
  //   return (
  //     <>
  //       <Header showBackButton />
  //       <MineOnboarding />
  //     </>
  //   );
  // }

  return (
    <>
      <Header showBackButton>{`${t('mine_squad_title')} ${
        info ? `${info.squad.level}lvl` : ''
      }`}</Header>
      <ScrollView footer={renderFooterButton()}>{renderContent()}</ScrollView>
    </>
  );
};
